import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { externalCompanyFieldHint, externalCompanyFieldLabel } from '@brimo/shared';
import { Banner, Button, Dialog, Select, TextField, Toggle, useToast } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { usePermissions, useWorkspace } from '../../../../lib/session';
import { useUserDirectory } from '../../../users/directory';
import { usePatchUser, useRoles, useTeams, type PatchUserBody, type UserTableRow } from './api';
import {
  MODULE_OFF_TEXT,
  toolModuleActive,
  toolsBody,
  useSaveUserTools,
  userToolItems,
  type UserTools,
} from './tools';
import styles from './UsersPage.module.css';

interface EditUserDialogProps {
  row: UserTableRow | null;
  onClose: () => void;
  onDeactivate: (row: UserTableRow) => void;
}

/**
 * „Bearbeiten" of a user row (Einstellungen 27, own dialog): Rolle, Team, Vorgesetzte(r), Funktion, Externe Firma →
 * `PATCH /users/:id` with the changed fields; the five tool switches (PKV M0) → `PUT /users/:id/tools` with the changed
 * tools; links to Admin 02 („Rechte anpassen") and Admin 06
 * („Deaktivieren …"). A sales lead (`settings.organization = team`) changes team, manager and the external company only (D-229).
 */
export function EditUserDialog({ row, onClose, onDeactivate }: EditUserDialogProps) {
  const open = row !== null;
  const navigate = useNavigate();
  const { toast } = useToast();
  const permissions = usePermissions();
  const fullAdmin = permissions.is('settings.organization', 'yes');
  const roles = useRoles(open);
  const teams = useTeams(open);
  const directory = useUserDirectory({}, open);
  const patch = usePatchUser();
  const saveTools = useSaveUserTools();
  const workspace = useWorkspace();
  const [tools, setTools] = useState<UserTools | null>(null);
  const [roleId, setRoleId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [externalCompany, setExternalCompany] = useState('');
  const [banner, setBanner] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const person = useMemo(
    () => (row ? (directory.data ?? []).find((user) => user.id === row.id) : undefined),
    [directory.data, row],
  );

  useEffect(() => {
    if (!row) return;
    setRoleId(row.roleId);
    setTeamId(row.teamId ?? '');
    setManagerId(row.managerId ?? '');
    setExternalCompany(row.externalCompany ?? '');
    setTools(row.tools);
    setBanner(null);
    setFields({});
  }, [row]);

  useEffect(() => {
    setJobTitle(person?.jobTitle ?? '');
  }, [person]);

  const managerOptions = useMemo(
    () => [
      { value: '', label: 'Keine Zuordnung' },
      ...(directory.data ?? [])
        .filter((user) => user.id !== row?.id)
        .map((user) => ({ value: user.id, label: user.name })),
    ],
    [directory.data, row],
  );
  const teamOptions = useMemo(
    () => [
      { value: '', label: 'Kein Team' },
      ...(teams.data ?? []).map((team) => ({ value: team.id, label: team.name })),
    ],
    [teams.data],
  );
  const roleOptions = useMemo(
    () => (roles.data ?? []).map((role) => ({ value: role.id, label: role.name })),
    [roles.data],
  );

  if (!row) return null;
  const current = row;

  async function submit(event: FormEvent) {
    event.preventDefault();
    const body: PatchUserBody = {};
    if (fullAdmin && roleId !== current.roleId) body.roleId = roleId;
    if (teamId !== (current.teamId ?? '')) body.teamId = teamId === '' ? null : teamId;
    if (managerId !== (current.managerId ?? '')) body.managerId = managerId === '' ? null : managerId;
    const title = jobTitle.trim();
    if (fullAdmin && title !== (person?.jobTitle ?? '')) body.jobTitle = title === '' ? null : title;
    const company = externalCompany.trim();
    if (company !== (current.externalCompany ?? '')) body.externalCompany = company === '' ? null : company;
    const changedTools = current.tools && tools ? toolsBody(current.tools, tools) : null;
    const patchNeeded = Object.keys(body).length > 0;
    if (!patchNeeded && changedTools === null) {
      onClose();
      return;
    }
    setBanner(null);
    setFields({});
    try {
      if (patchNeeded) await patch.mutateAsync({ id: current.id, body });
      if (changedTools) await saveTools.mutateAsync({ id: current.id, body: changedTools });
      toast({ message: patchNeeded ? `${current.name} gespeichert.` : 'Tools gespeichert.' });
      onClose();
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setFields(error.fields);
      else setBanner(errorMessage(error));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Nutzer bearbeiten"
      title={current.name}
      subtitle={current.email}
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" form="edit-user-form" loading={patch.isPending || saveTools.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form id="edit-user-form" className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        <div className={styles.formGrid}>
          <Select
            label="Rolle"
            value={roleId}
            options={roleOptions.length > 0 ? roleOptions : [{ value: roleId, label: current.roleLabel }]}
            disabled={!fullAdmin}
            error={fields['roleId']}
            onChange={(event) => setRoleId(event.target.value)}
          />
          <TextField
            label="Funktion"
            value={jobTitle}
            disabled={!fullAdmin}
            error={fields['jobTitle']}
            onChange={(event) => setJobTitle(event.target.value)}
          />
          <Select
            label="Team"
            value={teamId}
            options={teamOptions}
            error={fields['teamId']}
            onChange={(event) => setTeamId(event.target.value)}
          />
          <Select
            label="Vorgesetzte(r)"
            value={managerId}
            options={managerOptions}
            error={fields['managerId']}
            onChange={(event) => setManagerId(event.target.value)}
          />
        </div>
        <TextField
          label={externalCompanyFieldLabel}
          optional
          value={externalCompany}
          maxLength={120}
          hint={externalCompanyFieldHint}
          error={fields['externalCompany']}
          onChange={(event) => setExternalCompany(event.target.value)}
        />
        {tools ? (
          <div className={styles.form} role="group" aria-labelledby="edit-user-tools">
            <span id="edit-user-tools" className={styles.sectionLabel}>
              Tools
            </span>
            <div className={styles.toolSwitches}>
              {userToolItems.map(({ key, label }) => (
                <span key={key} className={styles.toolSwitch}>
                  <Toggle
                    size="large"
                    label={label}
                    checked={tools[key] === 'yes'}
                    onChange={(value) => setTools({ ...tools, [key]: value ? 'yes' : 'no' })}
                  />
                  {toolModuleActive(key, workspace.modules) ? null : (
                    <span className={styles.toolSwitchNote}>{MODULE_OFF_TEXT}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div className={styles.links}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => void navigate(`/settings/organization/permissions/${current.id}`)}
          >
            Rechte anpassen
          </button>
          {fullAdmin && current.status === 'active' ? (
            <button
              type="button"
              className={`${styles.linkButton} ${styles.linkDanger}`}
              onClick={() => onDeactivate(current)}
            >
              Deaktivieren …
            </button>
          ) : null}
        </div>
      </form>
    </Dialog>
  );
}
